import { platformService, trackingService } from '@monthio/react-components'
// @ts-ignore
import OpenReplay from '@openreplay/tracker'
// @ts-ignore
import trackerAssist from '@openreplay/tracker-assist'
import config from '#config'
import { setTag } from '@sentry/react'
import { intercomService } from './intercomService'

class OpenReplayService {
	private tracker?: OpenReplay
	private initialized = false
	private readonly initializeQueue: (() => void)[] = []
	public initialize = async () => {
		if (this.initialized || config.openReplayProject == null) {
			return
		}
		this.tracker = new OpenReplay({
			projectKey: config.openReplayProject,
			captureIFrames: true,
			disableStringDict: true,
			ingestPoint:
				platformService.getEnvironment() === 'production'
					? 'https://openreplay.monthio.systems/ingest'
					: 'https://openreplay.test-monthio.systems/ingest',
			network: {
				sessionTokenHeader: true,
				tokenUrlMatcher: (url) =>
					!url.endsWith('/asset-manifest.json') &&
					platformService.getEnvironment() !== 'development-cloud'
						? url.includes('monthio.com')
						: url.includes('monthio.dev'),
				capturePayload: true,
				useProxy: true,
				failuresOnly: false,
				captureInIframes: true,
				ignoreHeaders: ['Cookie', 'Set-Cookie'],
			},
		})

		try {
			const result = await this.tracker.start()
			if (!result.success) {
				throw new Error('Failed to start OpenReplay')
			}
			setTag('openReplaySessionToken', result.sessionToken)
			setTag('openReplaySessionURL', this.tracker?.getSessionURL())
			intercomService.setOpenReplayUrls(this.tracker?.getSessionURL())
			this.tracker.use(trackerAssist())
			trackingService.events.subscribe((event) => {
				this.tracker?.event(event.eventName, event.additional)
			})
			trackingService.errors.subscribe((errorEvent) => {
				const additional = {
					...(typeof errorEvent.additional === 'string'
						? { message: errorEvent.additional }
						: errorEvent.additional),
				}
				if (errorEvent.options.source != null) {
					additional.source = errorEvent.options.source
				}

				this.tracker?.handleError(errorEvent.error, additional)
			})
			trackingService.technicalEvents.subscribe((technicalEvent) => {
				const additional = {
					...(typeof technicalEvent.additional === 'string'
						? { message: technicalEvent.additional }
						: technicalEvent.additional),
				}
				if (technicalEvent.options.source != null) {
					additional.source = technicalEvent.options.source
				}

				this.tracker?.event(
					technicalEvent.eventName,
					additional,
					technicalEvent.options.treatAsError,
				)
			})
			this.initializeQueue.forEach((fn) => fn?.())

			// Subscribe to feature flag client and log feature flags
			this.initialized = true
		} catch (error) {
			console.error(error)
		}
	}

	public setUser(userId?: string | null) {
		if (userId == null) return

		if (!this.initialized) {
			this.initializeQueue.push(() => this.tracker?.setUserID(userId))
			return
		}

		this.tracker?.setUserID(userId)
	}

	public setMetaData(metaData: Record<string, any>) {
		if (!this.initialized) {
			this.initializeQueue.push(() =>
				Object.keys(metaData).forEach((key) => {
					if (metaData[key] == null) return
					this.tracker?.setMetadata(key, String(metaData[key]))
				}),
			)
			return
		}
		Object.keys(metaData).forEach((key) => {
			if (metaData[key] == null) return
			this.tracker?.setMetadata(key, String(metaData[key]))
		})
	}

	/**
	 * Stop the tracker and make it ready for a new session if needed in same session
	 */
	public stop = () => {
		this.tracker?.forceFlushBatch()
		sessionStorage?.setItem('__openreplay_reset', 't')
		this.tracker?.stop()
	}

	public getWebsocketTrackingHook = (connectionName: string) => {
		if (!this.initialized) {
			const queuedMessages: [string, string, 'up' | 'down'][] = []
			let hook = (msgType: string, data: string, dir: 'up' | 'down') => {
				queuedMessages.push([msgType, data, dir])
			}
			this.initializeQueue.push(() => {
				hook = this.tracker!.trackWs(connectionName)!
				queuedMessages.forEach(([msgType, data, dir]) =>
					hook(msgType, data, dir),
				)
			})
			return (msgType: string, data: string, dir: 'up' | 'down') =>
				hook(msgType, data, dir)
		}
		return this.tracker?.trackWs(connectionName)
	}
}

export const openReplayService = new OpenReplayService()
